import { addMinutes } from "date-fns";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { PersistOptions } from "zustand/middleware";
import type { Company, User } from "~/types/api";
import { parseJwt } from "~/utils/permission";

export interface UserState {
  user: User | null;
  company: Company | null;
  token: string | null;
  tokenExpiry?: number | null;
  permissions: string[] | null;
  sso: boolean;

  setSSO: (sso: boolean) => void;
  setUser: (user: User) => void;
  setToken: (token: string) => void;
  setCompany: (company: Company) => void;
  setPermissions: (permissions: string[]) => void;

  // Computed values
  isPermitted: (key: string, prefix?: string | undefined) => boolean;
  isLoggedIn: () => boolean;
  hasTokenExpired: () => boolean;

  reset: () => void;
}

const useUserState = create<UserState>()(
  persist(
    (set, get) => ({
      sso: false,
      user: null,
      company: null,
      token: null,
      permissions: null,
      tokenExpiry: null,

      setSSO: (sso: boolean) => set(() => ({ sso })),
      setUser: (user: User) => set(() => ({ user })),
      setCompany: (company: Company) => set(() => ({ company })),
      setToken: (token: string) =>
        set(() => {
          // Parse the token to extract expiry
          const decode = parseJwt(token);

          return {
            token,
            tokenExpiry: decode.exp,
          };
        }),
      setPermissions: (permissions: string[]) => set(() => ({ permissions })),

      isPermitted: (key: string, prefix: string = "Frontend.SO.") => {
        const permissions = get().permissions;
        if (!permissions) return false;

        const fullKey = `${prefix}${key}`;
        return permissions.includes(fullKey);
      },

      isLoggedIn: () => {
        const user = get().user;
        return !!user && !!user.userId && !!user.email;
      },

      hasTokenExpired: () => {
        const tokenExpiry = get().tokenExpiry;
        if (!tokenExpiry) return true;

        const currentTime = Math.floor(Date.now() / 1000);
        return currentTime >= tokenExpiry;
      },

      reset: () =>
        set(() => ({
          user: null,
          company: null,
          token: null,
          permissions: null,
        })),
    }),
    {
      name: "user-state",
    } as PersistOptions<UserState>,
  ),
);

export default useUserState;
